import { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Mail, Eye, RefreshCw } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { bulkMessageService } from '../../services/api';
import { Button, Modal, Badge, Skeleton, EmptyState, Toast } from '../../components/ui';
import { thStyle, tdStyle } from '../../utils/tableStyles';

const PAGE_SIZE = 20;

const CHANNEL_META = {
  whatsapp: { color: '#25D366', icon: MessageCircle, label: 'WhatsApp' },
  email:    { color: '#3B82F6', icon: Mail,          label: 'Email' },
};

const STATUS_META = {
  queued:               { color: '#94A3B8', label: 'Queued' },
  processing:           { color: '#3B82F6', label: 'Processing' },
  completed:            { color: '#22C55E', label: 'Completed' },
  completed_with_errors:{ color: '#F59E0B', label: 'Completed (errors)' },
  failed:               { color: '#EF4444', label: 'Failed' },
};

const ITEM_STATUS_COLOR = { sent: '#22C55E', failed: '#EF4444', skipped: '#F59E0B', pending: '#94A3B8', processing: '#3B82F6' };

const BulkCampaignHistory = () => {
  const { dark } = useTheme();
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterChannel, setFilterChannel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [toast, setToast] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await bulkMessageService.list({
        page, limit: PAGE_SIZE,
        channel: filterChannel || undefined,
        status: filterStatus || undefined,
      });
      setCampaigns(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Failed to load campaigns', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, filterChannel, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const cardStyle = {
    background: dark ? '#0F172A' : '#fff',
    border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
    borderRadius: 16, overflow: 'hidden',
  };
  const selectStyle = {
    padding: '7px 10px', borderRadius: 8, fontSize: 13,
    border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
    background: dark ? '#1E293B' : '#F8FAFC',
    color: dark ? '#F1F5F9' : '#0F172A', outline: 'none', cursor: 'pointer',
  };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Bulk Campaigns</h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>
            {total.toLocaleString()} bulk send{total === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <div style={cardStyle}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, padding: '14px 20px', flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
          <select value={filterChannel} onChange={e => { setFilterChannel(e.target.value); setPage(1); }} style={selectStyle}>
            <option value="">All Channels</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
          </select>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={selectStyle}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_META).map(([code, m]) => (
              <option key={code} value={code}>{m.label}</option>
            ))}
          </select>
          <button
            onClick={() => { setFilterChannel(''); setFilterStatus(''); setPage(1); load(); }}
            title="Refresh"
            style={{ ...selectStyle, marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
          >
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20 }}><Skeleton rows={6} cols={8} /></div>
          ) : campaigns.length === 0 ? (
            <div style={{ padding: 40 }}>
              <EmptyState title="No bulk campaigns yet" description="Select leads on the Leads page and send a bulk email or WhatsApp message to see it here." />
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                <tr>
                  <th style={thStyle(dark)}>Channel</th>
                  <th style={thStyle(dark)}>Template</th>
                  <th style={thStyle(dark)}>Total</th>
                  <th style={thStyle(dark)}>Sent</th>
                  <th style={thStyle(dark)}>Failed</th>
                  <th style={thStyle(dark)}>Skipped</th>
                  <th style={thStyle(dark)}>Status</th>
                  <th style={thStyle(dark)}>Created By</th>
                  <th style={thStyle(dark)}>Date</th>
                  <th style={thStyle(dark)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => {
                  const meta = CHANNEL_META[c.channel];
                  const statusMeta = STATUS_META[c.status] || { color: '#94A3B8', label: c.status };
                  const Icon = meta.icon;
                  return (
                    <tr key={c._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                      <td style={tdStyle(dark)}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Icon size={14} color={meta.color} /> {meta.label}
                        </span>
                      </td>
                      <td style={tdStyle(dark)}>{c.templateName || c.template?.name || '—'}</td>
                      <td style={tdStyle(dark)}>{c.totalCount}</td>
                      <td style={{ ...tdStyle(dark), color: '#22C55E', fontWeight: 600 }}>{c.sentCount}</td>
                      <td style={{ ...tdStyle(dark), color: c.failedCount ? '#EF4444' : undefined, fontWeight: c.failedCount ? 600 : undefined }}>{c.failedCount}</td>
                      <td style={tdStyle(dark)}>{c.skippedCount}</td>
                      <td style={tdStyle(dark)}><Badge color={statusMeta.color}>{statusMeta.label}</Badge></td>
                      <td style={tdStyle(dark)}>{c.createdBy ? `${c.createdBy.firstName} ${c.createdBy.lastName || ''}` : '—'}</td>
                      <td style={{ ...tdStyle(dark), fontSize: 11 }}>{new Date(c.createdAt).toLocaleString()}</td>
                      <td style={tdStyle(dark)}>
                        <button
                          title="View details"
                          onClick={() => setDetailId(c._id)}
                          style={{ padding: 6, borderRadius: 6, border: `1px solid ${dark ? '#475569' : '#E2E8F0'}`, background: 'transparent', cursor: 'pointer', color: dark ? '#94A3B8' : '#475569', display: 'flex' }}
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
            <span style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <CampaignDetailModal campaignId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
};

// ── Detail modal — campaign summary + per-lead item list ────────────────────
const CampaignDetailModal = ({ campaignId, onClose }) => {
  const { dark } = useTheme();
  const [campaign, setCampaign] = useState(null);
  const [items, setItems] = useState([]);
  const [itemStatus, setItemStatus] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!campaignId) { setCampaign(null); setItems([]); setItemStatus(''); return; }
    setLoading(true);
    bulkMessageService.getCampaign(campaignId)
      .then(({ data }) => setCampaign(data.data || data))
      .catch(() => setCampaign(null))
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    bulkMessageService.getCampaignItems(campaignId, { status: itemStatus || undefined, limit: 200 })
      .then(({ data }) => setItems(data.data || []))
      .catch(() => setItems([]));
  }, [campaignId, itemStatus]);

  const border = dark ? '#334155' : '#E2E8F0';
  const sub = dark ? '#94A3B8' : '#64748B';
  const text = dark ? '#F1F5F9' : '#0F172A';
  const selectStyle = {
    padding: '6px 10px', borderRadius: 8, fontSize: 12,
    border: `1px solid ${border}`, background: dark ? '#1E293B' : '#F8FAFC',
    color: text, outline: 'none', cursor: 'pointer',
  };

  return (
    <Modal open={!!campaignId} onClose={onClose} title="Campaign Details" width={640}>
      {loading || !campaign ? (
        <Skeleton rows={4} cols={1} />
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Total', value: campaign.totalCount, color: sub },
              { label: 'Sent', value: campaign.sentCount, color: '#22C55E' },
              { label: 'Failed', value: campaign.failedCount, color: '#EF4444' },
              { label: 'Skipped', value: campaign.skippedCount, color: '#F59E0B' },
            ].map(s => (
              <div key={s.label} style={{ flex: 1, textAlign: 'center', padding: '10px 6px', borderRadius: 10, border: `1px solid ${border}`, background: dark ? '#1E293B' : '#F8FAFC' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: sub }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: sub }}>Filter:</span>
            <select value={itemStatus} onChange={e => setItemStatus(e.target.value)} style={selectStyle}>
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div style={{ border: `1px solid ${border}`, borderRadius: 10, maxHeight: 320, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: sub, fontSize: 13 }}>No leads in this filter</div>
            ) : (
              items.map(it => (
                <div key={it._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${border}` }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: text }}>{it.leadName || '—'}</div>
                    {it.destination && <div style={{ fontSize: 11, color: sub }}>{it.destination}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <Badge color={ITEM_STATUS_COLOR[it.status] || '#94A3B8'}>{it.status}</Badge>
                    {it.error && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 4, maxWidth: 260 }}>{it.error}</div>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BulkCampaignHistory;
